import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Archive } from 'lucide-react';
import { useState } from 'react';
import { Button, Dialog } from '~/components/primitives';
import { Select } from '~/components/primitives/Select';
import { fetchProjectApprovers, fetchProjectArchivePreview } from '~/lib/api/projects';
import { useAuthStore } from '~/store/auth';
import { ApproverEmptyNote, hasNoApproverCandidates } from './ApproverEmptyNote';
import styles from './CancelProjectDialog.module.css';

/**
 * F7 (project-actions-parity §3.2) — архивация получает ту же защиту, что отмена.
 *
 * Защита была перевёрнута относительно ущерба: отмена, которая с задачами не делала
 * НИЧЕГО, требовала подписи и обоснования от десяти символов; архивация — та, что
 * архивирует два десятка задач и (до B10) безвозвратно удаляла строки членства, —
 * уходила по одному подтверждению без единой подписи. При этом `emitAudit` для
 * архивации уже писал `tier: 2`: журнал утверждал то, чего не было.
 *
 * Разметка общая с диалогом отмены намеренно: это одно семейство решений над
 * проектом, и своя развела бы их визуально на ровном месте.
 */
/**
 * F1 (tier2-approval-requests §5) — подписи в теле больше нет: при Tier 2 уходит
 * ЗАЯВКА названному адресату, и объект не меняется до его решения.
 */
type ArchivePayload = {
  reason: string;
  /** Адресат заявки: действие Tier 2 без него не отправляется. */
  addressee_user_id?: string;
};

const MIN_REASON = 10;

export function ArchiveProjectDialog(props: {
  open: boolean;
  projectId: string;
  projectName: string;
  projectCode: string | null;
  onConfirm: (payload: ArchivePayload) => void;
  onCancel: () => void;
}): JSX.Element {
  const currentUserId = useAuthStore((s) => s.user?.user_id ?? s.user?.id ?? '');
  const [reason, setReason] = useState('');
  const [approverUserId, setApproverUserId] = useState('');
  const reasonOk = reason.trim().length >= MIN_REASON;
  const approverOk = !!approverUserId && approverUserId !== currentUserId;
  const valid = reasonOk && approverOk;

  /** Превью судьбы задач — то же, что показывала прежняя секция настроек. */
  const previewQuery = useQuery({
    queryKey: ['project-archive-preview', props.projectId],
    queryFn: () => fetchProjectArchivePreview(props.projectId),
    enabled: props.open,
    staleTime: 30_000,
  });
  const preview = previewQuery.data;

  const approversQuery = useQuery({
    queryKey: ['project-approvers', 'project.archive'],
    queryFn: () => fetchProjectApprovers({ action: 'project.archive' }),
    enabled: props.open,
    staleTime: 60_000,
  });
  const approverOptions = (approversQuery.data?.items ?? [])
    .filter((a) => a.id !== currentUserId)
    .map((a) => ({ value: a.id, label: a.full_name ?? a.email }));
  /**
   * F8 (project-plan-change §5) — «кандидатов нет» приходит ОТ СЕРВЕРА кодом
   * `NO_APPROVER_CANDIDATES`, а не выводится из пустого списка. Пустой массив мог
   * означать и «ещё грузится», и «поиск ничего не нашёл»; код означает ровно одно,
   * и вместе с ним приходит перечень недостающих ролей.
   */
  const noApproverAvailable = hasNoApproverCandidates(approversQuery.data);

  const blockReason = noApproverAvailable
    ? null
    : !approverOk
      ? m.approval_request_block_addressee()
      : !reasonOk
        ? m.project_cancel_block_reason()
        : null;

  return (
    <Dialog open={props.open} onClose={props.onCancel} title={m.project_archive_title()}>
      <div className={styles.body}>
        <div className={styles.subject}>
          <span className={styles.subjectName}>{props.projectName}</span>
          {props.projectCode !== null && (
            <span className={styles.subjectCode}>{props.projectCode}</span>
          )}
        </div>

        {/* Гейт фаз C/D — блок нейтральный: после B10 архивация ОБРАТИМА, а сам
            блок сообщает отсутствие ущерба («задачи и связи сохраняются»). Красить
            это в `--warn` значит употреблять семантический цвет там, где отклонения
            нет (§1.3). Жёлтое осталось у отмены — она необратима по задачам. */}
        <div className={`${styles.impact} ${styles.impactInfo}`}>
          <span className={styles.impactTitle}>
            <Archive size={15} aria-hidden="true" />
            {m.project_cancel_impact_title()}
          </span>
          {preview ? (
            <ul className={styles.impactList}>
              {preview.archive_count > 0 && (
                <li className={styles.impactNum}>
                  {m.tm_archive_exclusive({ n: preview.archive_count })}
                </li>
              )}
              {preview.promote_count > 0 && (
                <li>{m.tm_archive_promote({ n: preview.promote_count })}</li>
              )}
              {preview.detach_count > 0 && (
                <li>{m.tm_archive_detach({ n: preview.detach_count })}</li>
              )}
              {/* B10 сделал архивацию обратимой — и диалог обязан это сказать:
                  до него архивация удаляла членства безвозвратно, и «убрать с глаз»
                  на деле означало «потерять». */}
              <li>{m.project_archive_impact_reversible()}</li>
              {/* Гейт фаз C/D — своя строка. Здесь стояло «Отмена применится сразу»:
                  чужое слово ровно там, где различие «отменить»/«архивировать»
                  обязано читаться до клика. */}
              <li>{m.project_archive_impact_immediate()}</li>
            </ul>
          ) : (
            <span className={styles.impactPending}>{m.project_cancel_impact_pending()}</span>
          )}
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {m.project_archive_reason_label()}
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          </span>
          <textarea
            className={styles.textarea}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            aria-describedby="archive-reason-hint"
          />
          <span className={styles.hint} id="archive-reason-hint">
            {m.project_cancel_reason_hint({ min: MIN_REASON })}
          </span>
        </label>

        <Select
          label={m.approval_request_addressee_label()}
          value={approverUserId}
          onChange={(e) => setApproverUserId(e.target.value)}
          options={approverOptions}
          placeholder={m.approval_request_addressee_placeholder()}
          required
          disabled={noApproverAvailable}
        />
        <span className={styles.hint}>
          {noApproverAvailable ? (
            <ApproverEmptyNote data={approversQuery.data} />
          ) : (
            m.approval_request_addressee_hint()
          )}
        </span>

        <div className={styles.blockReasonRow} role="status" aria-live="polite">
          {blockReason !== null && <span className={styles.blockReason}>{blockReason}</span>}
        </div>

        <div className={styles.footer}>
          <Button variant="ghost" onClick={props.onCancel}>
            {m.project_archive_keep()}
          </Button>
          <Button
            // Отправка заявки не разрушительна: алая кнопка обещала бы не то,
            // что произойдёт.
            variant="primary"
            disabled={!valid}
            onClick={() => {
              props.onConfirm({ reason, addressee_user_id: approverUserId });
            }}
          >
            {m.approval_request_cta()}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
